import React from 'react';

interface PageHeaderProps {
  /** Small uppercase category label (e.g. "ACCOUNT", "CAMPAIGNS") */
  category?: string;
  /** The main page title — renders as H1 with Instrument Sans */
  title: string;
  /** Optional subtitle / description */
  subtitle?: string;
  /** Optional right-side content (buttons, filters, etc.) */
  actions?: React.ReactNode;
}

/**
 * Omni Ad Signature Page Header
 * ─────────────────────────────
 * Consistent branded header for all non-Dashboard pages.
 *
 * Design system rationale:
 * - 3px gradient stripe (Electric Purple → Neon Green) = brand signature
 * - Category pill = wayfinding + color presence
 * - Clean H1 = Instrument Sans hierarchy
 * - Subtitle = contextual guidance
 *
 * Usage:
 *   <PageHeader
 *     category="ACCOUNT"
 *     title="Settings"
 *     subtitle="Manage your profile, preferences, and notifications."
 *   />
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  category,
  title,
  subtitle,
  actions,
}) => {
  return (
    <div className="mb-8 lg:mb-10">
      {/* ── Signature Gradient Stripe ── */}
      <div
        className="h-[3px] rounded-full mb-6 lg:mb-8"
        style={{
          background: 'linear-gradient(90deg, #be43ff 0%, #a9f600 100%)',
        }}
      />

      {/* ── Header Content ── */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="space-y-2">
          {/* Category Pill */}
          {category && (
            <span
              className="inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] tracking-[0.08em] bg-[#be43ff]/8 text-[#be43ff]"
              style={{ fontWeight: 600 }}
            >
              {category}
            </span>
          )}

          {/* Page Title — Instrument Sans via <h1> base styles */}
          <h1 className="text-[#101828]">{title}</h1>

          {/* Subtitle */}
          {subtitle && (
            <p className="text-[#667085] text-sm max-w-xl leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>

        {/* Optional Actions */}
        {actions && (
          <div className="flex items-center gap-3 shrink-0">{actions}</div>
        )}
      </div>
    </div>
  );
};

/**
 * Omni Ad Section Title
 * ─────────────────────
 * Use for h3-level section headings within a page.
 * Left-border accent in Electric Purple creates branded structure.
 *
 * Usage:
 *   <SectionTitle title="Login Email" subtitle="Link multiple emails..." />
 */
interface SectionTitleProps {
  title: string;
  subtitle?: string;
}

export const SectionTitle: React.FC<SectionTitleProps> = ({ title, subtitle }) => {
  return (
    <div className="pl-4 border-l-[3px] border-[#be43ff]">
      <h3 className="text-[#101828]" style={{ fontWeight: 600 }}>
        {title}
      </h3>
      {subtitle && (
        <p className="text-[#667085] text-sm leading-relaxed mt-1 max-w-xs">
          {subtitle}
        </p>
      )}
    </div>
  );
};
